import { useCallback, useEffect, useRef, useState } from 'react';
import Echo from 'laravel-echo';

const POLL_FALLBACK_MS = 30000;

// The event was deleted (404/410) or the signed url no longer validates (403):
// retrying can never succeed, so the wall stops instead of polling forever.
const GONE_STATUSES = [403, 404, 410];

export const DEFAULT_SETTINGS = {
    theme: 'cork',
    cardSize: 'medium',
    rotation: 'subtle',
    showCaptions: true,
    showNames: true,
    showQr: true,
    slideDuration: 10,
};

function normaliseItem(raw) {
    return {
        id: raw.id,
        file_type: raw.file_type,
        url: raw.url,
        preview_url: raw.previewUrl ?? raw.preview_url ?? null,
        mp4_url: raw.shareUrl ?? raw.share_url ?? null,
        blurhash: raw.blurhash ?? null,
        uploader_name: raw.user?.name ?? raw.uploader_name ?? '',
        caption: raw.caption ?? null,
        is_landscape: raw.is_landscape ?? false,
        isNew: raw.isNew ?? false,
    };
}

/**
 * Shared live wall data source: seeds from the server-provided media list,
 * receives new media over websockets when available and falls back to
 * polling the signed reload endpoint. Used by both the interactive wall
 * page and the Cast/TV build. Pass `onSettingsUpdated` to also receive
 * wall settings broadcast by the presenter (`settings.updated`).
 * `gone` turns true once the event no longer exists; polling and the
 * websocket stop at that point.
 */
export default function useLiveWallItems({ initialMedia, reloadUrl, wsChannel, wsAuthUrl, onSettingsUpdated }) {
    const [items, setItems] = useState(() => (initialMedia?.data ?? initialMedia ?? []).map(normaliseItem));
    const [newItemCount, setNewItemCount] = useState(0);
    const [gone, setGone] = useState(false);

    const pollTimer = useRef(null);
    const isMounted = useRef(true);

    // Ref so a new callback identity doesn't tear down the websocket
    const settingsCallback = useRef(onSettingsUpdated);
    useEffect(() => { settingsCallback.current = onSettingsUpdated; }, [onSettingsUpdated]);

    useEffect(() => {
        return () => { isMounted.current = false; };
    }, []);

    const prependItem = useCallback((raw) => {
        const item = normaliseItem({ ...raw, isNew: true });
        setItems(prev => {
            if (prev.some(p => p.id === item.id)) return prev;
            return [item, ...prev];
        });
        setNewItemCount(prev => prev + 1);
    }, []);

    // Simulate a new photo arrival (dev helper)
    const simulatePhoto = useCallback(() => {
        setItems(prev => {
            if (!prev.length) return prev;
            const source = prev[Math.floor(Math.random() * prev.length)];
            const fakeId = `sim-${Date.now()}`;
            const sim = { ...source, id: fakeId, isNew: true };
            return [sim, ...prev];
        });
        setNewItemCount(prev => prev + 1);
    }, []);

    // WebSocket — initialised lazily only on pages using this hook
    useEffect(() => {
        if (gone || !wsChannel || !import.meta.env.VITE_REVERB_APP_KEY) return;

        let echo = null;
        try {
            echo = new Echo({
                broadcaster: 'reverb',
                key: import.meta.env.VITE_REVERB_APP_KEY,
                wsHost: import.meta.env.VITE_REVERB_HOST ?? 'localhost',
                wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
                wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
                forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'http') === 'https',
                enabledTransports: ['ws', 'wss'],
                wsPath: '/ws',
                authEndpoint: wsAuthUrl,
            });
            echo.private(wsChannel)
                .listen('.media.added', prependItem)
                .listen('.settings.updated', (payload) => {
                    if (settingsCallback.current && payload?.settings) {
                        settingsCallback.current(payload.settings);
                    }
                });
        } catch {
            // Polling fallback handles updates
        }

        return () => {
            try { echo?.leave(wsChannel); echo?.disconnect(); } catch {}
        };
    }, [gone, wsChannel, wsAuthUrl, prependItem]);

    // Polling fallback
    const poll = useCallback(async () => {
        if (!reloadUrl) return;
        let keepPolling = true;
        try {
            const response = await axios.get(reloadUrl);
            const incoming = (response.data?.data ?? response.data ?? []).map(normaliseItem);
            setItems(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newOnes = incoming.filter(p => !existingIds.has(p.id)).map(p => ({ ...p, isNew: true }));
                if (!newOnes.length) return prev;
                setNewItemCount(c => c + 1);
                return [...newOnes, ...prev];
            });
        } catch (error) {
            if (GONE_STATUSES.includes(error?.response?.status)) {
                keepPolling = false;
                if (isMounted.current) setGone(true);
            }
        } finally {
            if (keepPolling && isMounted.current) {
                pollTimer.current = setTimeout(poll, POLL_FALLBACK_MS);
            }
        }
    }, [reloadUrl]);

    useEffect(() => {
        pollTimer.current = setTimeout(poll, POLL_FALLBACK_MS);
        return () => clearTimeout(pollTimer.current);
    }, [poll]);

    return { items, newItemCount, simulatePhoto, gone };
}
